import { useMemo } from 'react';
import {
  type AnalyticsCompare,
  type AnalyticsGranularity,
  type AnalyticsSection,
  dimensionFor,
} from '#shared/types';
import { useUrlState } from '#client/hooks/useUrlState';
import {
  type AnalyticsFilterValues,
  DEFAULT_PRESET,
  type IdParam,
  type RangePreset,
  filterQuery,
  hasDimensionFilter,
  joinIds,
  parsePreset,
  readFilterValues,
  resolvePresetRange,
} from './filters.logic';

/**
 * Every analytics filter, held in the query string.
 *
 * In the URL for the reason the worklist's filters are: "last quarter, the
 * chemistry bench" has to be a link somebody can paste into a message, and it
 * has to survive a refresh and step properly under Back.
 *
 * The rule that keeps those links readable is that a parameter is written only
 * when it differs from the default. So the canonical URL for "the last thirty
 * days, everything" is the bare `/analytics/volume`, not six parameters all
 * saying "as usual".
 */

interface AnalyticsFilterSetters {
  setPreset: (preset: RangePreset) => void;
  setRange: (from: string, to: string) => void;
  setBy: (by: string) => void;
  setCompare: (compare: AnalyticsCompare) => void;
  setGranularity: (granularity: AnalyticsGranularity | undefined) => void;
  setIncludeApproximate: (include: boolean) => void;
  setIds: (key: IdParam, ids: number[]) => void;
  clearAll: () => void;
}

export interface AnalyticsFilterState extends AnalyticsFilterValues, AnalyticsFilterSetters {
  from: string;
  to: string;
  preset: RangePreset;
  /** What goes on the wire. Every key omitted when it is at its default. */
  query: Record<string, string | undefined>;
  /** True when any dimension filter is narrowing the data. */
  isFiltered: boolean;
}

const CLEARABLE_PARAMS = [
  'preset',
  'from',
  'to',
  'compare',
  'granularity',
  'approximate',
  'department_ids',
  'test_ids',
  'panel_ids',
  'user_ids',
];

export function useAnalyticsFilters(section: AnalyticsSection): AnalyticsFilterState {
  const { params, update, setParam, clearParams } = useUrlState();

  const preset = parsePreset(params.get('preset'));

  // Read out of the URL *before* the memo, so the dependency list is plain
  // values the lint rule can check statically. Inline `params.get(…)` calls in
  // a dependency array are opaque to it, and it is right to complain: the memo
  // must recompute when the URL changes, not when the component happens to
  // re-render, and only named values make that legible.
  const rawFrom = params.get('from');
  const rawTo = params.get('to');
  const resolved = useMemo(
    () => resolvePresetRange(preset, { from: rawFrom, to: rawTo }, new Date()),
    [preset, rawFrom, rawTo],
  );

  const values = readFilterValues(params, section);

  const setters: AnalyticsFilterSetters = {
    setPreset: (next) =>
      update((draft) => {
        if (next === DEFAULT_PRESET) draft.delete('preset');
        else draft.set('preset', next);
        // A preset computes its own dates, so leaving the old explicit ones
        // behind would silently override the preset the user just picked.
        if (next !== 'custom') {
          draft.delete('from');
          draft.delete('to');
        }
      }),
    setRange: (from, to) =>
      update((draft) => {
        draft.set('preset', 'custom');
        draft.set('from', from);
        draft.set('to', to);
      }),
    setBy: (next) => setParam('by', next === dimensionFor(section, undefined) ? undefined : next),
    setCompare: (next) => setParam('compare', next === 'none' ? undefined : next),
    setGranularity: (next) => setParam('granularity', next),
    setIncludeApproximate: (include) => setParam('approximate', include ? 'true' : undefined),
    setIds: (key, next) => setParam(key, joinIds(next)),
    clearAll: () => clearParams(CLEARABLE_PARAMS),
  };

  return {
    ...resolved,
    preset,
    ...values,
    ...setters,
    query: filterQuery({ ...resolved, by: params.get('by') ?? undefined }, values),
    isFiltered: hasDimensionFilter(values),
  };
}
